/**
 * Retry utility for API calls with exponential backoff
 */

// Shape of the errors this module inspects: standard Error fields plus the
// optional `response` some HTTP clients attach. Avoids `any` while still
// covering network errors, thrown `Error`s, and response-bearing failures.
type RetryableError = Error & {
  response?: { status?: number; statusText?: string; data?: { message?: string } };
};

export interface RetryOptions {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
  shouldRetry?: (error: RetryableError) => boolean;
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
  shouldRetry: (error: RetryableError) => {
    // Retry on network errors or 5xx server errors
    if (!error.response) return true; // Network error
    const status = error.response?.status;
    return status !== undefined && status >= 500 && status < 600;
  },
};

/**
 * Retry a function with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let lastError: unknown;
  let delay = opts.initialDelay;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry if we've exhausted attempts
      if (attempt === opts.maxRetries) {
        break;
      }

      // Don't retry if error is not retryable
      if (!opts.shouldRetry(error)) {
        break;
      }

      // Wait before retrying
      console.log(`Retry attempt ${attempt + 1}/${opts.maxRetries} after ${delay}ms`);
      await sleep(delay);

      // Exponential backoff with max delay cap
      delay = Math.min(delay * opts.backoffMultiplier, opts.maxDelay);
    }
  }

  throw lastError;
}

/**
 * Sleep utility
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Wrap an API function with retry logic
 */
export function withRetry<T extends (...args: never[]) => Promise<unknown>>(
  fn: T,
  options?: RetryOptions
): T {
  return ((...args: Parameters<T>) => {
    return retryWithBackoff(() => fn(...args), options);
  }) as T;
}

/**
 * Get user-friendly error message from error object
 */
export function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;

  const e = error as RetryableError;

  if (e?.message) return e.message;

  if (e?.response?.data?.message) return e.response.data.message;

  if (e?.response?.statusText) return e.response.statusText;

  if (!e?.response) return 'Network error - please check your connection';

  const status = e?.response?.status;
  if (status === 404) return 'Resource not found';
  if (status === 403) return 'Access denied';
  if (status === 401) return 'Authentication required';
  if (status !== undefined && status >= 500) return 'Server error - please try again';

  return 'An unexpected error occurred';
}

/**
 * Check if error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  return DEFAULT_OPTIONS.shouldRetry(error as RetryableError);
}
